import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

// `[&_svg]:pointer-events-none` — lucide-react icons are SVG with
// their own event target. In Firefox a click on the icon lands on the
// <svg> rather than the <button>, which can make hover flicker and
// swallow onClick handlers if an ancestor treats SVGs specially. We
// never want the icon to receive events — the button is the control.
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        outline: 'border border-border bg-background hover:bg-muted',
        ghost: 'hover:bg-muted',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-10 px-6',
        icon: 'h-8 w-8',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, type, ...props }, ref) => (
    // Default `type` to 'button'. HTML's default is 'submit', which
    // triggers form submission if the button is ever nested in a
    // <form>. We don't use forms here but the default has caused
    // silent "click does nothing" symptoms across the app.
    <button
      ref={ref}
      type={type ?? 'button'}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  ),
);
Button.displayName = 'Button';

/**
 * Class-name helper for non-button elements that want to look like a
 * `<Button>` — e.g. a `<Link>` for in-app navigation. Apply via
 * `className={buttonClasses({ variant: 'outline', size: 'sm' })}`.
 */
export const buttonClasses = buttonVariants;
